import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoiceItem } from '../../../types/invoiceTypes';
import { ProductData } from '../../../types/offerTypes';
import { useAuth } from '../../../context/AuthContext';
import { db } from '../../../firebaseConfig';
import { collection, query, where, getDocs } from 'firebase/firestore';
import logger from '../../../utils/logger';
import { useProductManagement } from '../../../hooks/invoice/useProductManagement';
import { enhanceInvoiceItemText } from '../../../api/invoiceApi';
import { PlusIcon } from '@heroicons/react/24/outline';
import MobileInvoiceItemRow from './MobileInvoiceItemRow';
import {
  DEFAULT_TAX_CATEGORY_SELECTION,
  getTaxCategoryEntry,
  resolveExemptionReason,
  type InvoiceTaxCategoryKey,
} from '../../../utils/taxCategoryCatalog';

// VB-1 Feature-Flag (build-time, fail-closed, Default AUS).
const TAX_CATEGORY_ENABLED = process.env.REACT_APP_TAX_CATEGORY_ENABLED === 'true';

// Tracking, welcher Preis zuletzt eingegeben wurde
interface PriceTracking {
  [itemId: string]: 'net' | 'gross';
}

interface MobileInvoiceItemsFormProps {
  items: InvoiceItem[];
  setItems: React.Dispatch<React.SetStateAction<InvoiceItem[]>>;
  currency: string;
  showTitle?: boolean;
  /** VB-1: rechnungsweiter Steuerart-Default für neue Positionen (nur bei aktivem Flag genutzt). */
  defaultTaxCategorySelection?: InvoiceTaxCategoryKey | null;
  /** VB-1: Käufer-USt-IdNr. — für Inline-Hinweis bei K/AE-Positionen. */
  buyerVatId?: string | null;
}

// Interface für KI-Vorschlag
interface AISuggestion {
  originalName: string;
  originalDescription?: string | null;
  suggestedName: string;
  suggestedDescription?: string | null;
}

// Custom Hook für mobile Produktvorschläge
function useMobileProductSuggestions(productNameInputs: { [id: string]: string }, userId: string | undefined) {
  const [allSuggestions, setAllSuggestions] = useState<{ [itemId: string]: ProductData[] }>({});
  
  React.useEffect(() => {
    if (!userId) {
      setAllSuggestions({});
      return;
    }
    
    const fetchSuggestions = async () => {
      try {
        // Lade alle Produkte einmal
        const col = collection(db, 'products');
        const q = query(col, where('userId', '==', userId));
        const snapshot = await getDocs(q);
        
        const allProducts = snapshot.docs.map(doc => ({ 
          productId: doc.id, 
          ...doc.data() 
        } as ProductData));
        
        // Filtere für jedes Item basierend auf dem Suchtext
        const suggestions: { [itemId: string]: ProductData[] } = {};
        
        Object.entries(productNameInputs).forEach(([itemId, searchValue]) => {
          if (!searchValue || searchValue.length < 2) {
            suggestions[itemId] = [];
            return;
          }
          
          const searchLowerCase = searchValue.toLowerCase();
          const matchingProducts = allProducts
            .filter(product => {
              const productName = product.name?.toString() || '';
              return productName.toLowerCase().includes(searchLowerCase);
            })
            .slice(0, 5); // Beschränke auf 5 Ergebnisse für mobile
          
          suggestions[itemId] = matchingProducts;
        });
        
        setAllSuggestions(suggestions);
        logger.debug({totalSuggestions: Object.keys(suggestions).length}, "mobile produktvorschläge geladen");
      } catch (error) {
        logger.error(error, "fehler beim abrufen der mobilen produkt-vorschläge");
        setAllSuggestions({});
      }
    };
    
    // Debounce für bessere Performance
    const timeoutId = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [userId, productNameInputs]);
  
  return allSuggestions;
}

const MobileInvoiceItemsForm: React.FC<MobileInvoiceItemsFormProps> = ({
  items,
  setItems,
  currency,
  showTitle = true,
  defaultTaxCategorySelection,
  buyerVatId,
}) => {
  const { t } = useTranslation('invoice');
  const { currentUser, sellerData, effectiveScopeUid } = useAuth();
  const [lastEditedPrice, setLastEditedPrice] = useState<PriceTracking>({});
  const [productNameInputs, setProductNameInputs] = useState<{ [id: string]: string }>({});
  const [showProductDropdowns, setShowProductDropdowns] = useState<{ [id: string]: boolean }>({});
  const [aiSuggestion, setAiSuggestion] = useState<AISuggestion | null>(null);
  const [enhancingItemId, setEnhancingItemId] = useState<string | null>(null);
  const [isLoadingSuggestion, setIsLoadingSuggestion] = useState<boolean>(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState<boolean>(false);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);

  // Initialize the product management hook
  const {
    handleProductSelection: hookHandleProductSelection,
  } = useProductManagement();

  // Initialisiere productNameInputs
  useEffect(() => {
    const updatedInputs: { [id: string]: string } = {};
    items.forEach(item => {
      if (!productNameInputs[item.id] || productNameInputs[item.id] !== item.name) {
        updatedInputs[item.id] = item.name;
      }
    });

    if (Object.keys(updatedInputs).length > 0) {
      setProductNameInputs(prev => ({
        ...prev,
        ...updatedInputs
      }));
    }
  }, [items]);

  // Initialisiere lastEditedPrice für neue Items
  useEffect(() => {
    const newItemsWithoutTracking = items.filter(item => !lastEditedPrice[item.id]);
    
    if (newItemsWithoutTracking.length > 0) {
      const newTracking: PriceTracking = {};
      newItemsWithoutTracking.forEach(item => {
        if (item.grossPrice !== undefined && item.grossPrice !== null && item.grossPrice > 0) {
          newTracking[item.id] = 'gross';
        } else {
          newTracking[item.id] = 'net';
        }
      });
      
      setLastEditedPrice(prev => ({
        ...prev,
        ...newTracking
      }));
    }
  }, [items, lastEditedPrice]);

  // Berechnungsfunktionen
  const roundToTwoDecimals = (value: number): number => {
    return Math.round(value * 100) / 100;
  };

  const calculateGrossPrice = (netPrice: number | undefined | null, taxRate: number): number => {
    return (netPrice || 0) * (1 + taxRate / 100);
  };

  const calculateNetPrice = (grossPrice: number | undefined | null, taxRate: number): number => {
    if (taxRate === -100) return 0; 
    return (grossPrice || 0) / (1 + taxRate / 100);
  };

  const getAccurateSingleNetPrice = (item: InvoiceItem, trackingState: PriceTracking): number => {
    const lastEdited = trackingState[item.id] || 'net'; 
    if (lastEdited === 'gross') {
      return calculateNetPrice(item.grossPrice, item.taxRate);
    } else {
      return item.netPrice || 0;
    }
  };

  const getAccurateItemNetAmount = (item: InvoiceItem, trackingState: PriceTracking): number => {
    const singleNetPrice = getAccurateSingleNetPrice(item, trackingState);
    return item.quantity * singleNetPrice;
  };

  const getAccurateItemTaxAmount = (item: InvoiceItem, trackingState: PriceTracking): number => {
    const netAmount = getAccurateItemNetAmount(item, trackingState);
    return netAmount * (item.taxRate / 100);
  };

  const calculateItemNetAmountForTotal = (item: InvoiceItem): number => { 
    return getAccurateItemNetAmount(item, lastEditedPrice); 
  };

  const calculateItemTaxAmountForTotal = (item: InvoiceItem): number => { 
    return getAccurateItemTaxAmount(item, lastEditedPrice); 
  };
  
  // Berechne präzise Summen
  const preciseNetTotal = items.reduce((sum, item) => sum + calculateItemNetAmountForTotal(item), 0);
  const preciseTaxTotal = items.reduce((sum, item) => sum + calculateItemTaxAmountForTotal(item), 0);
  const preciseGrossTotal = preciseNetTotal + preciseTaxTotal; 

  // Runde Summen nur für die Anzeige
  const displayGrossTotal = roundToTwoDecimals(preciseGrossTotal);
  const displayTaxTotal = roundToTwoDecimals(preciseTaxTotal);
  const displayNetTotal = roundToTwoDecimals(displayGrossTotal - displayTaxTotal);

  // Gruppiere Steuern nach Satz
  const taxesByRate = items.reduce((acc, item) => {
    const rate = item.taxRate;
    const netAmount = calculateItemNetAmountForTotal(item);
    const taxAmount = calculateItemTaxAmountForTotal(item);
    if (!acc[rate]) {
      acc[rate] = { net: 0, tax: 0 };
    }
    acc[rate].net += netAmount;
    acc[rate].tax += taxAmount;
    return acc;
  }, {} as { [key: number]: { net: number; tax: number } });

  // Formatierungsfunktion für Währung
  const formatCurrency = (amount: number) => {
    const validCurrency = currency.length === 3 ? currency : 'EUR';
    try {
      return new Intl.NumberFormat('de-DE', { 
        style: 'currency', 
        currency: validCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      logger.error(error, `Formatierungsfehler mit Währungscode ${validCurrency}`);
      return new Intl.NumberFormat('de-DE', { 
        style: 'decimal',
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      }).format(amount) + ' ' + validCurrency;
    }
  };

  // Funktion zum Hinzufügen einer neuen Position
  const handleAddItem = () => {
    let defaultTax = 19.0;
    // Kleinunternehmer → 0 % als Standard
    if (sellerData?.isSmallBusiness) {
      defaultTax = 0.0;
    } else if (sellerData?.defaultTaxRate !== undefined && sellerData?.defaultTaxRate !== null) {
      defaultTax = sellerData.defaultTaxRate;
    }
    const defaultUnit = sellerData?.defaultUnitCode || 'H87';

    // VB-1: Steuerart-Default für neue Positionen (nur bei aktivem Flag).
    const defaultSelection = TAX_CATEGORY_ENABLED
      ? (defaultTaxCategorySelection || DEFAULT_TAX_CATEGORY_SELECTION)
      : undefined;
    const defaultEntry = getTaxCategoryEntry(defaultSelection);
    // Bei 0 %-Default den gesperrten Satz übernehmen, sonst den Seller-Default.
    const effectiveTax = defaultEntry?.ratesLocked ? defaultEntry.allowedRates[0] : defaultTax;

    const newItem: InvoiceItem = {
      id: crypto.randomUUID(),
      name: '',
      quantity: 1,
      netPrice: 0,
      taxRate: effectiveTax,
      unitPrice: 0,
      unitCode: defaultUnit,
      description: '',
      itemAction: 'create',
      productId: null,
      // VB-1: Steuerart-Felder vorbelegen (additiv; undefined wenn Flag aus).
      ...(defaultEntry && {
        taxCategorySelection: defaultEntry.selection,
        taxCategory: defaultEntry.bt151Code,
        exemptionReason: resolveExemptionReason(defaultEntry.selection),
      }),
    };
    
    const newItemId = newItem.id;
    
    setItems([...items, newItem]);
    
    const newInputs = { ...productNameInputs, [newItemId]: '' };
    const newDropdowns = { ...showProductDropdowns, [newItemId]: false };
    const newPriceTracking = { ...lastEditedPrice, [newItemId]: 'net' as 'net' };
    
    setProductNameInputs(newInputs);
    setShowProductDropdowns(newDropdowns); 
    setLastEditedPrice(newPriceTracking);
  };

  // Funktion zum Entfernen einer Position
  const handleRemoveItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
    setLastEditedPrice(prev => {
      const updatedTracking = { ...prev };
      delete updatedTracking[id];
      return updatedTracking;
    });
    setProductNameInputs(prev => {
      const updatedInputs = { ...prev };
      delete updatedInputs[id];
      return updatedInputs;
    });
    setShowProductDropdowns(prev => {
      const updatedDropdowns = { ...prev };
      delete updatedDropdowns[id];
      return updatedDropdowns;
    });
  };

  // Item-Änderung Handler
  const handleItemChange = (id: string, field: keyof Omit<InvoiceItem, 'id'>, value: any) => {
    let lastEditedType: 'net' | 'gross' | undefined = undefined;
    
    setItems(prevItems => {
      const updatedItems = prevItems.map(item => {
        if (item.id === id) {
          let updatedItem = { ...item, [field]: value };

          if (field === 'netPrice') {
             updatedItem.grossPrice = calculateGrossPrice(value, item.taxRate);
             lastEditedType = 'net';
          } else if (field === 'grossPrice') {
             updatedItem.netPrice = calculateNetPrice(value, item.taxRate);
             lastEditedType = 'gross';
          } else if (field === 'taxRate') {
            const currentEditedType = lastEditedPrice[id];
            if(currentEditedType === 'gross' && item.grossPrice !== null && item.grossPrice !== undefined) {
                 updatedItem.netPrice = calculateNetPrice(item.grossPrice, value);
            } else if (item.netPrice !== null && item.netPrice !== undefined) {
                 updatedItem.grossPrice = calculateGrossPrice(item.netPrice, value);
            }
          }

          return updatedItem;
        }
        return item;
      });
      
      return updatedItems;
    });
    
    if (lastEditedType) {
      setTimeout(() => {
        setLastEditedPrice(prev => ({ ...prev, [id]: lastEditedType as 'net' | 'gross' }));
      }, 0);
    }
  };

  // Berechnet den Nettobetrag für eine Position
  const calculateItemNetAmount = (item: InvoiceItem): number => {
     return getAccurateItemNetAmount(item, lastEditedPrice); 
  };

  // KI-Enhancement Handler
  const handleEnhanceClick = async (itemId: string) => {
    const itemToEnhance = items.find(item => item.id === itemId);
    if (!itemToEnhance || !currentUser) return;

    setEnhancingItemId(itemId); 
    setIsLoadingSuggestion(true);
    setSuggestionError(null);

    try {
      const token = await currentUser.getIdToken();
      const result = await enhanceInvoiceItemText(itemToEnhance.name, itemToEnhance.description || '', token);
      setAiSuggestion({ 
        originalName: itemToEnhance.name,
        originalDescription: itemToEnhance.description,
        suggestedName: result.suggestedName, 
        suggestedDescription: result.suggestedDescription 
      });
      setShowSuggestionModal(true); 
    } catch (err) {
      logger.error(err, "Error enhancing item text");
      setSuggestionError(err instanceof Error ? err.message : "Fehler bei der KI-Verbesserung.");
      setEnhancingItemId(null);
    } finally {
      setIsLoadingSuggestion(false);
    }
  };

  // Modal-Handler
  const closeSuggestionModal = () => {
    setShowSuggestionModal(false);
    setAiSuggestion(null);
    setEnhancingItemId(null);
  };
  
  const handleAcceptSuggestion = () => {
    if (!aiSuggestion || !enhancingItemId) return;
    
    try {
      const itemIndex = items.findIndex(item => item.id === enhancingItemId);
      if (itemIndex === -1) return;
      
      const updatedItems = [...items];
      updatedItems[itemIndex] = {
        ...updatedItems[itemIndex],
        name: aiSuggestion.suggestedName,
        description: aiSuggestion.suggestedDescription || ''
      };
      
      setItems(updatedItems);
      
      setProductNameInputs(prev => ({
        ...prev,
        [enhancingItemId]: aiSuggestion.suggestedName
      }));
      
      closeSuggestionModal();
    } catch (error) {
      logger.error("Fehler beim Akzeptieren des Vorschlags:", error);
    }
  };

  const handleRejectSuggestion = () => {
    closeSuggestionModal();
  };

  // Produktname-Änderung Handler
  const handleProductNameChange = (id: string, value: string) => {
    setProductNameInputs(prev => ({ ...prev, [id]: value }));
    handleItemChange(id, 'name', value);
    setShowProductDropdowns(prev => ({ ...prev, [id]: true }));
  };

  // Produktauswahl Handler
  const handleSelectProductSuggestion = (itemId: string, suggestion: ProductData) => {
    setItems(prevItems =>
      prevItems.map(item => {
        if (item.id === itemId) {
          const newNetPrice = suggestion.netPrice ?? item.netPrice;
          const newTaxRate = suggestion.taxRate ?? item.taxRate;

          const updatedItem = {
            ...item,
            name: suggestion.name || '',
            description: suggestion.description || suggestion.name || '',
            netPrice: newNetPrice,
            taxRate: newTaxRate,
            unitCode: suggestion.unitCode || item.unitCode,
            sellerArticleId: suggestion.sellerArticleId || item.sellerArticleId || null,
            buyerArticleId: suggestion.buyerArticleId || item.buyerArticleId || null,
            globalId: suggestion.globalId || item.globalId || null,
            globalIdScheme: suggestion.globalIdScheme || item.globalIdScheme || null,
            ...(suggestion.productId && { productId: suggestion.productId }),
            grossPrice: calculateGrossPrice(newNetPrice, newTaxRate),
          };
          
          return updatedItem;
        }
        return item;
      })
    );
    
    setLastEditedPrice(prev => ({ ...prev, [itemId]: 'net' }));
    setShowProductDropdowns(prev => ({ ...prev, [itemId]: false }));
    setProductNameInputs(prev => ({ ...prev, [itemId]: suggestion.name || '' }));
    hookHandleProductSelection(itemId, suggestion); 
  };

  // Mobile Produktvorschläge Hook
  const allProductSuggestions = useMobileProductSuggestions(productNameInputs, effectiveScopeUid);

  return (
    <div className={showTitle ? "bg-surface-raised rounded-lg border border-hairline p-4 mb-6" : ""}>
      {showTitle && (
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-primary">{t('positions.title')}</h2>
          <span className="text-sm text-muted">
            {items.length} {items.length === 1 ? 'Position' : 'Positionen'}
          </span>
        </div>
      )}

      {/* Mobile-optimierte Item-Liste */}
      <div className="space-y-4">
        {items.map((item, index) => (
          <MobileInvoiceItemRow
            key={item.id}
            item={item}
            index={index}
            onItemChange={handleItemChange}
            onRemove={handleRemoveItem}
            currency={currency}
            productNameInput={productNameInputs[item.id] ?? item.name}
            setProductNameInput={(value) => handleProductNameChange(item.id, value)}
            showProductDropdown={showProductDropdowns[item.id] ?? false}
            setShowProductDropdown={(show) => setShowProductDropdowns(prev => ({...prev, [item.id]: show}))}
            onSelectProductSuggestion={(suggestion) => handleSelectProductSuggestion(item.id, suggestion)}
            userId={effectiveScopeUid}
            lastEditedPrice={lastEditedPrice[item.id]}
            onEnhanceClick={handleEnhanceClick}
            isLoadingSuggestion={isLoadingSuggestion && enhancingItemId === item.id}
            formatCurrency={formatCurrency}
            calculateItemNetAmount={calculateItemNetAmount}
            calculateGrossPrice={calculateGrossPrice}
            calculateNetPrice={calculateNetPrice}
            productSuggestions={allProductSuggestions[item.id] || []}
            buyerVatId={buyerVatId}
          />
        ))}
      </div>

      {/* Position hinzufügen Button - Mobile optimiert */}
      <button 
        type="button" 
        onClick={handleAddItem} 
        className="w-full mt-6 px-4 py-4 border-2 border-dashed border-indigo-500 text-accent-ink rounded-lg hover:bg-[var(--tone-accent-bg)] transition text-base font-medium flex items-center justify-center space-x-2"
      >
        <PlusIcon className="h-5 w-5" />
        <span>{t('positions.addPosition')}</span>
      </button>

      {/* Mobile-optimierte Gesamtsummen */}
      <div className="mt-8 p-4 bg-surface-inset rounded-lg border border-strong">
        <h3 className="text-lg font-semibold mb-4 text-primary">{t('totals.title')}</h3>
        <div className="space-y-3">
          {/* Nettosummen pro Steuersatz */}
          {Object.entries(taxesByRate).map(([rate, amounts]) => (
            <div key={rate} className="flex justify-between items-center text-secondary">
              <span className="text-sm">{t('totals.subtotalVat', { rate })}</span>
              <span className="font-medium">{formatCurrency(roundToTwoDecimals(amounts.net))}</span>
            </div>
          ))}
          
          <div className="flex justify-between items-center text-secondary py-2 border-t border-strong">
            <span className="font-medium">{t('totals.totalNet')}</span>
            <span className="font-medium">{formatCurrency(displayNetTotal)}</span>
          </div>
          
          {/* Steuersummen pro Steuersatz */}
          {Object.entries(taxesByRate).map(([rate, amounts]) => (
            amounts.tax > 0 && (
              <div key={rate} className="flex justify-between items-center text-secondary">
                <span className="text-sm">{t('totals.vat')} ({rate}%)</span>
                <span className="font-medium">{formatCurrency(roundToTwoDecimals(amounts.tax))}</span>
              </div>
            )
          ))}
          
          <div className="flex justify-between items-center font-bold text-primary text-lg pt-3 border-t border-strong">
            <span>{t('totals.totalGross')}</span>
            <span>{formatCurrency(displayGrossTotal)}</span>
          </div>
        </div>
      </div>

      {/* KI-Vorschlag Modal - Mobile optimiert */}
      {showSuggestionModal && aiSuggestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('aiSuggestion.title')}</h3>
              
              {suggestionError ? (
                <p className="text-red-600 mb-4">{suggestionError}</p>
              ) : aiSuggestion ? (
                <div className="space-y-4 mb-6">
                  <div>
                    <p className="text-sm font-medium text-faint mb-2">{t('aiSuggestion.originalName')}</p>
                    <p className="text-sm text-gray-800 p-3 bg-gray-100 rounded">{aiSuggestion.originalName}</p>
                    <p className="text-sm font-medium text-faint mt-3 mb-2">{t('aiSuggestion.suggestedName')}</p>
                    <p className="text-sm text-green-800 p-3 bg-green-50 rounded font-semibold">{aiSuggestion.suggestedName}</p>
                  </div>
                  
                  {(aiSuggestion.originalDescription || aiSuggestion.suggestedDescription) && (
                    <div className="pt-4 border-t">
                      <p className="text-sm font-medium text-faint mb-2">{t('aiSuggestion.originalDescription')}</p>
                      <p className="text-sm text-gray-800 p-3 bg-gray-100 rounded">{aiSuggestion.originalDescription || '-'}</p>
                      <p className="text-sm font-medium text-faint mt-3 mb-2">{t('aiSuggestion.suggestedDescription')}</p>
                      <p className="text-sm text-green-800 p-3 bg-green-50 rounded font-semibold">{aiSuggestion.suggestedDescription || '-'}</p>
                    </div>
                  )}
                </div>
              ) : null}
              
              {/* Buttons - Mobile optimiert */}
              <div className="flex flex-col space-y-3">
                {!suggestionError && aiSuggestion && (
                  <button
                    type="button"
                    className="w-full py-3 px-4 bg-green-600 text-white rounded-md font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                    onClick={handleAcceptSuggestion}
                  >
                    {t('aiSuggestion.accept')}
                  </button>
                )}
                <button
                  type="button"
                  className="w-full py-3 px-4 bg-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  onClick={handleRejectSuggestion}
                >
                  {suggestionError ? t('aiSuggestion.close') : t('aiSuggestion.reject')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileInvoiceItemsForm;
